import { AppThunkP } from 'utils/frontendTypes';
import { DataKeys } from 'reducers/data';
import { REDUX_ACTIONS } from 'utils/constants';
import {
  Organizations,
  OrgUsers,
  Kits,
  ReferenceLists,
  ReferenceListTypes,
  ResponsePayload,
  SearchQuery,
  ShortenedUrlCreateParams,
  ShortenedUrls,
  SuccessResult,
  Templates,
  FeatureableDiscoverItem,
  Hubs,
  ConversationPrompts,
  HubUpsertParams,
  UpdateFeaturedDiscoverItemsRequestBody,
  SuggestedTopics,
  DiscoverItem,
} from 'utils/gloo-types';
import * as ROUTES from './routes';
import request, { DEFAULT_HEADERS, updateDeviceData } from './utils';

const { REACT_APP_API_BASE_URL } = process.env;

const CHECK_CACHE_TIME = 5 * 60 * 1000; // 5 minutes
const LONG_CACHE_TIME = 60 * 60 * 1000; // 60 minutes

export function setData(key: DataKeys, data: any) {
  return { type: REDUX_ACTIONS.SET_DATA, key, data };
}

function updateData(key: DataKeys, data: any) {
  return { type: REDUX_ACTIONS.UPDATE_DATA, key, data };
}

const LONG_CACHE_KEYS: DataKeys[] = [];

function checkCache(key: DataKeys): AppThunkP {
  return (dispatch, getState): any | any[] | null => {
    const arr = getState().data[key];
    // Data exists
    // return null;
    if (arr) {
      const cacheTime = getState().data.lastCachedTime[key];
      const now = new Date().valueOf();
      if (cacheTime) {
        // Could be a long cache type
        const checkTime = LONG_CACHE_KEYS.includes(key) ? LONG_CACHE_TIME : CHECK_CACHE_TIME;
        if (now - cacheTime < checkTime) {
          return arr;
        }
      }
    }
    return null;
  };
}

export function getOrganization(orgId: number): AppThunkP<Organizations> {
  return async (dispatch) => {
    const result = await dispatch(request({ ...ROUTES.GET_ORGANIZATION, params: { orgId }, pathParams: { orgId } }));
    return result;
  };
}

export function deleteCloudinaryMedia(image_public_ids: string[]): AppThunkP {
  return async (dispatch) => {
    const result = await dispatch(request({ ...ROUTES.POST_IMAGE_DELETE, data: { image_public_ids } }));
    // eslint-disable-next-line no-console
    console.info(`deleted image`, result);
    return result;
  };
}

export function apiLogout(): AppThunkP<ResponsePayload<SuccessResult>> {
  return async (dispatch) => {
    return dispatch(request({ ...ROUTES.LOGOUT }));
  };
}

export function getMe(orgId?: string): AppThunkP<{
  user: OrgUsers;
  organization: Organizations;
  accessOrgs: Partial<Organizations>[];
}> {
  return async (dispatch) => {
    const params: { orgId?: string } = {};
    if (orgId) params.orgId = orgId;
    const { user, organization, accessOrgs } = await dispatch(request({ ...ROUTES.GET_ME, params }));
    if (user) {
      dispatch({ type: REDUX_ACTIONS.UPDATE_ME, orgUser: user, organization, accessOrgs });

      // Fire and forget (dont await this) update me with geo ip data
      dispatch(updateDeviceData());
    }

    return { user, organization, accessOrgs };
  };
}

export function reloadMe(): AppThunkP<undefined | { orgUser: OrgUsers; organization: Organizations }> {
  return async (dispatch, getState) => {
    if (!getState().auth.isLoggedIn) return;
    const { user: orgUser, organization } = await dispatch(getMe());
    return { orgUser, organization };
  };
}

export function getReferenceListValuesByType(
  type: ReferenceListTypes,
  isGlobalReferenceList?: 'true' | 'false',
  options?: { force?: boolean },
): AppThunkP<ReferenceLists[]> {
  return async (dispatch) => {
    if (!options?.force) {
      if (type === ReferenceListTypes.GROUPS) {
        const cached = dispatch(checkCache('groups'));
        if (cached) {
          return cached;
        }
      } else if (type === ReferenceListTypes.TAGS) {
        const cached = dispatch(checkCache('tags'));
        if (cached) {
          return cached;
        }
      } else if (type === ReferenceListTypes.TOPICS) {
        const cached = dispatch(checkCache('topics'));
        if (cached) {
          return cached;
        }
      }
    }

    const results: ReferenceLists[] = await dispatch(
      request({ ...ROUTES.GET_REFERENCE_LISTS, params: { type, isGlobalReferenceList } }),
    );

    switch (type) {
      case ReferenceListTypes.GROUPS:
        return dispatch(setData('groups', results));
      case ReferenceListTypes.TAGS:
        return dispatch(setData('tags', results));
      case ReferenceListTypes.TOPICS:
        return dispatch(setData('topics', results));
    }
  };
}

export function shortenUrlViaGlooApi(url: string): AppThunkP<ResponsePayload<ShortenedUrls>> {
  return async (dispatch) =>
    dispatch(request({ ...ROUTES.SHORTEN_URL_VIA_GLOO_API, data: { longUrl: url } as ShortenedUrlCreateParams }));
}

export function getFeaturedDiscoverItems(): AppThunkP<FeatureableDiscoverItem[]> {
  return async (dispatch) => dispatch(request({ ...ROUTES.GET_FEATURED_DISCOVER_ITEMS }));
}

export function setFeaturedDiscoverItems(
  data: UpdateFeaturedDiscoverItemsRequestBody,
): AppThunkP<FeatureableDiscoverItem[]> {
  return async (dispatch) => dispatch(request({ ...ROUTES.SET_FEATURED_DISCOVER_ITEMS, data }));
}

export function getAllArchivedHubs(): AppThunkP<Hubs[]> {
  return async (dispatch) => dispatch(request({ ...ROUTES.GET_ARCHIVED_HUBS }));
}

export function getAllArchivedKits(): AppThunkP<Kits[]> {
  return async (dispatch) => dispatch(request({ ...ROUTES.GET_ARCHIVED_KITS }));
}

export function getAllArchivedTemplates(): AppThunkP<Templates[]> {
  return async (dispatch) => dispatch(request({ ...ROUTES.GET_ARCHIVED_TEMPLATES }));
}

export function getAllHubs(): AppThunkP<Hubs[]> {
  return async (dispatch) => dispatch(request({ ...ROUTES.GET_HUBS }));
}

export function getHub(hubId: string, options?: { errorToastFilters: string[] }): AppThunkP<Hubs> {
  return async (dispatch) =>
    dispatch(request({ ...ROUTES.GET_HUB, pathParams: { hubId }, errorToastFilters: options?.errorToastFilters }));
}

export function createHub(data: HubUpsertParams): AppThunkP<Hubs> {
  return async (dispatch) => dispatch(request({ ...ROUTES.CREATE_HUB, data }));
}

export function updateHub(hubId: string, data: HubUpsertParams): AppThunkP<Hubs> {
  return async (dispatch) => dispatch(request({ ...ROUTES.UPDATE_HUB, pathParams: { hubId }, data }));
}

export function archiveHub(hubId: string): AppThunkP<Hubs> {
  return async (dispatch) => dispatch(request({ ...ROUTES.ARCHIVE_HUB, pathParams: { hubId } }));
}

export function unarchiveHub(hubId: string): AppThunkP<Hubs> {
  return async (dispatch) => dispatch(request({ ...ROUTES.UNARCHIVE_HUB, pathParams: { hubId } }));
}

export function getAllKits(): AppThunkP<Kits[]> {
  return async (dispatch) => dispatch(request({ ...ROUTES.GET_KITS }));
}

export function getKit(kitId: string, options?: { errorToastFilters: string[] }): AppThunkP<Kits> {
  return async (dispatch) =>
    dispatch(request({ ...ROUTES.GET_KIT, pathParams: { kitId }, errorToastFilters: options?.errorToastFilters }));
}

export function createKit(data: Partial<Kits>): AppThunkP {
  return async (dispatch) => dispatch(request({ ...ROUTES.CREATE_KIT, data }));
}

export function updateKit(kitId: string, data: Partial<Kits>): AppThunkP {
  return async (dispatch) => dispatch(request({ ...ROUTES.UPDATE_KIT, pathParams: { kitId }, data }));
}

export function archiveKit(kitId: string): AppThunkP<Kits> {
  return async (dispatch) => dispatch(request({ ...ROUTES.ARCHIVE_KIT, pathParams: { kitId } }));
}

export function unarchiveKit(kitId: string): AppThunkP<Kits> {
  return async (dispatch) => dispatch(request({ ...ROUTES.UNARCHIVE_KIT, pathParams: { kitId } }));
}

export function getKitParents(kitId: string): AppThunkP<DiscoverItem[]> {
  return async (dispatch) => dispatch(request({ ...ROUTES.GET_KIT_PARENTS, pathParams: { kitId } }));
}

export function getAllTemplates(): AppThunkP<Templates[]> {
  return async (dispatch) => dispatch(request({ ...ROUTES.GET_TEMPLATES }));
}

export function getTemplate(templateId: string): AppThunkP<Templates> {
  return async (dispatch) => dispatch(request({ ...ROUTES.GET_TEMPLATE, pathParams: { templateId } }));
}

export function createTemplate(data: Partial<Templates>): AppThunkP {
  return async (dispatch) => dispatch(request({ ...ROUTES.CREATE_TEMPLATE, data }));
}

export function updateTemplate(templateId: string, data: Partial<Templates>): AppThunkP {
  return async (dispatch) => dispatch(request({ ...ROUTES.UPDATE_TEMPLATE, pathParams: { templateId }, data }));
}

export function archiveTemplate(templateId: string): AppThunkP<Templates> {
  return async (dispatch) => dispatch(request({ ...ROUTES.ARCHIVE_TEMPLATE, pathParams: { templateId } }));
}

export function unarchiveTemplate(templateId: string): AppThunkP<Templates> {
  return async (dispatch) => dispatch(request({ ...ROUTES.UNARCHIVE_TEMPLATE, pathParams: { templateId } }));
}

export function getTemplateParents(templateId: string): AppThunkP<DiscoverItem[]> {
  return async (dispatch) => dispatch(request({ ...ROUTES.GET_TEMPLATE_PARENTS, pathParams: { templateId } }));
}

export function updateConversationPrompt(promptId: string, prompt: Partial<ConversationPrompts>): AppThunkP {
  return async (dispatch) => {
    const results = await dispatch(
      request({
        ...ROUTES.UPDATE_CONVERSATION_PROMPT,
        pathParams: { promptId },
        data: { ...prompt },
      }),
    );

    dispatch(updateData('conversationPrompts', results));

    return results;
  };
}

export function createConversationPrompt(prompt: Partial<ConversationPrompts>): AppThunkP {
  return async (dispatch) => {
    const results = await dispatch(
      request({
        ...ROUTES.CREATE_CONVERSATION_PROMPT,
        data: prompt,
      }),
    );
    return results;
  };
}

export async function loginViaApi({ idToken, refreshToken }: { idToken: string; refreshToken: string }) {
  return fetch(`${REACT_APP_API_BASE_URL}auth/login`, {
    method: 'POST',
    headers: DEFAULT_HEADERS,
    body: JSON.stringify({
      idToken,
      refreshToken,
    }),
    credentials: 'include',
  });
}

export async function fetchAuthStatus() {
  return fetch(`${REACT_APP_API_BASE_URL}auth/status`, {
    method: 'POST',
    headers: DEFAULT_HEADERS,
    credentials: 'include',
  });
}

export function getConversationPrompt(id: string): AppThunkP<ConversationPrompts> {
  return async (dispatch) => dispatch(request({ ...ROUTES.GET_CONVERSATION_PROMPT, pathParams: { id } }));
}

export function queryConversationPrompts(
  currentPrompts: ConversationPrompts[],
  params: SearchQuery & { showArchived?: boolean },
): AppThunkP<{ conversationPrompts: ConversationPrompts[]; count: number }> {
  return async (dispatch) => {
    const { conversationPrompts, count } = await dispatch(
      request({ ...ROUTES.QUERY_CONVERSATION_PROMPTS, data: params }),
    );
    return { conversationPrompts, count };
  };
}

export function getAllConversationPromptAuthors(): AppThunkP<string[]> {
  return async (dispatch) =>
    dispatch(
      request({
        ...ROUTES.GET_ALL_CONVERSATION_PROMPT_AUTHORS,
      }),
    );
}

export function getAllSuggestedTopics(): AppThunkP<SuggestedTopics[]> {
  return async (dispatch) => {
    const results = await dispatch(
      request({
        ...ROUTES.GET_ALL_SUGGESTED_TOPICS,
      }),
    );

    return results;
  };
}
